import { FormikErrors, FormikProps, FormikTouched } from "formik";

import { SearchItem } from "@storeblocks/autosuggest";
import { Option } from "@storeblocks/select";
import {
  AKADEMIKERNE_SPECIFIC_PRODUCTS,
  HUSEIERNE_SPECIFIC_PRODUCTS,
  ProductBackendName,
} from "Components/Features/CheckPrice/Products/productTypes";
import { TextManager } from "Components/TextContext";
import { ErrorPrefix } from "Constants/constants";
import { Company } from "Store/companies/interfaces";
import { getCompanyNames } from "Store/companies/utils";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { ManualProduct } from "Store/interfaces/products/checkout/ManualProduct";
import { ProductType } from "Store/interfaces/products/checkout/ProductType";
import { ValuablesProduct } from "Store/interfaces/products/checkout/riskProducts/ValuablesProduct";
import { CabinProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/CabinProduct";
import { HouseProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/HouseProduct";
import get from "lodash.get";
import { addDefaultSelectOption } from "./formUtils";
import { isDefined } from "./functions";

/**
 * Returns the formik error for the property if property is touched and error is set
 */
export function getFormikErrorIfTouched<T>(
  property: keyof T,
  errors: FormikErrors<T>,
  touched: FormikTouched<T>,
): any | undefined {
  return touched[property] ? errors[property] : undefined;
}

/**
 * Returns the formik error for the property if it exists,
 * if property has been touched, or the form has at least one attempted submit.
 */
export function getError<T>(
  property: keyof T,
  form: FormikProps<any>,
): any | undefined {
  const errorMessage = String(form.errors[property]);
  const isBreakingError = errorMessage.startsWith(ErrorPrefix.BREAKING);

  if (!isBreakingError && form.touched[property]) {
    return form.errors[property];
  }

  return undefined;
}

/**
 * Gets a nested formik error using a string containing the path to the property.
 *
 * @param propertyPath Path to property using normal JavaScript syntax
 * @param form Formik form
 * @returns Error message, if any
 */
export const getNestedError = (
  propertyPath: string,
  form: FormikProps<any>,
): any | undefined => {
  const errorMessage = String(get(form.errors, propertyPath));
  const isBreakingError = errorMessage.startsWith(ErrorPrefix.BREAKING);

  if (!isBreakingError && get(form.touched, propertyPath)) {
    return get(form.errors, propertyPath);
  }

  return undefined;
};

export function getBreakingError<T, V extends keyof T>(
  property: V,
  form: FormikProps<T>,
): string | undefined {
  const errorMessage = form.errors[property];
  if (
    typeof errorMessage === "string" &&
    errorMessage.startsWith(ErrorPrefix.BREAKING) &&
    form.touched[property]
  ) {
    return errorMessage.slice(ErrorPrefix.BREAKING.length);
  }

  return undefined;
}

export const companiesAsSearchItems = (list: Company[] | null): SearchItem[] =>
  isDefined(list) ? getCompanyNames(list) : [];

export const companiesAsSearchItemsWithOrgNumber = (
  companies: Company[] | null,
): SearchItem[] =>
  isDefined(companies)
    ? companies.map((company: Company) => ({
        label: company.name,
        value: company.organizationNumber,
      }))
    : [];

export const companiesAsOptions = (
  defaultLabel: string,
  list: Company[] | null,
): Option[] =>
  addDefaultSelectOption(
    defaultLabel,
    list?.map(
      (company: Company): Option => ({
        label: company.name,
        value: company.name,
      }),
    ) ?? [],
  );

export function showCoverageGroup(
  product: InsuranceProduct,
  isChildCoverageExtraHalfGActive: boolean,
): boolean {
  return !(
    product.name === INSURANCE_NAME_VALUE.ACCIDENT ||
    product.name === INSURANCE_NAME_VALUE.CRITICAL_ILLNESS ||
    product.name === INSURANCE_NAME_VALUE.DEATH ||
    product.name === INSURANCE_NAME_VALUE.DISABILITY_FLEX ||
    product.name === INSURANCE_NAME_VALUE.VALUABLES ||
    product.name === INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN ||
    product.name === INSURANCE_NAME_VALUE.ANIMAL ||
    product.name === INSURANCE_NAME_VALUE.RENTAL ||
    (isChildCoverageExtraHalfGActive &&
      product.name === INSURANCE_NAME_VALUE.NEW_CHILD) ||
    product.name === INSURANCE_NAME_VALUE.CHILD_HEALTH ||
    product.name.includes("AKA_") ||
    !product.coverageGroup
  );
}

export function coverageGroupName(
  product: InsuranceProduct,
  productText: any,
  lang: string,
): string {
  let coverageGroupText = "";

  /**
   * Make sure that missing FMS stuff doesn't crash the app.
   */
  try {
    if (product.coverageGroup) {
      coverageGroupText =
        productText.types.coverageGroup[product.coverageGroup][lang];
    }
  } catch {
    // do nothing
  }

  switch (product.name) {
    case INSURANCE_NAME_VALUE.HEALTH:
    case INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING:
      return ` - ${coverageGroupText.split(" ")[1]}`;
    case INSURANCE_NAME_VALUE.HOUSE:
    case INSURANCE_NAME_VALUE.CABIN:
      return getBuildingCoverageGroupText(product, coverageGroupText);
    case INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE:
      return "";
    default:
      return ` - ${coverageGroupText}`;
  }
}

export const isHuseierneSpecificProduct = (
  productBackendName: ProductBackendName,
): boolean => HUSEIERNE_SPECIFIC_PRODUCTS.includes(productBackendName);

export const isAkademikerneSpecificProduct = (
  productBackendName: ProductBackendName,
): boolean => AKADEMIKERNE_SPECIFIC_PRODUCTS.includes(productBackendName);

export function isManualProduct(
  product: ProductType,
): product is ManualProduct {
  return product.name === INSURANCE_NAME_VALUE.MANUAL;
}

function getBuildingCoverageGroupText(
  product: HouseProduct | CabinProduct,
  coverageGroupText: string,
): string {
  const { getText } = TextManager;

  if (product.onlyFireAndNatureDamage) {
    return ` - ${getText(
      "insuranceproduct.house.alternativecoveragegroup-firecoverage",
    )}`;
  } else {
    return ` - ${coverageGroupText}`;
  }
}

export function isValuablesProduct(
  product: ProductType,
): product is ValuablesProduct {
  return product.name === INSURANCE_NAME_VALUE.VALUABLES;
}
